<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  NButton, NEmpty, NSpin, NResult, NCard, NSpace, NAvatar,
  NDivider, NTag, NGrid, NGridItem, NAlert, NIcon, NModal,
  NUpload, NUploadDragger, NPopover, NProgress, NTable, NCollapse,
  NCollapseItem, NTimeline, NTimelineItem, NTooltip
} from 'naive-ui';
import imageCompression from 'browser-image-compression';
import {
  fetchProviderProfile,
  uploadProviderProfileImage,
  getProviderProfileImage,
  getProviderImages
} from '../services/provider';
import { authToken, isAuthenticated } from '../services/auth';
import {
  Camera,
  X as CloseIcon,
  Upload as UploadIcon,
  User,
  Edit as EditIcon,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Clock,
  Award,
  Briefcase,
  DollarSign,
  MessageSquare,
  Globe,
  Heart,
  Check,
  Video,
  Accessibility,
  Car,
  CreditCard,
  Languages as LanguagesIcon,
  School,
  Image as ImageIcon,
  FileText,
  Link,
  Scale,
  Gavel,
  BookOpen,
  Users
} from 'lucide-vue-next';

// State variables
const provider = ref(null);
const loading = ref(true);
const error = ref(null);
const router = useRouter();

// Profile image state variables
const profileImageUrl = ref(null);
const isUploadingImage = ref(false);
const imageUploadError = ref(null);
const showImageModal = ref(false);

// Provider images state
const facilityImages = ref([]);
const loadingImages = ref(false);

// Load provider profile when component mounts
onMounted(async () => {
  if (!isAuthenticated.value) {
    error.value = 'Please log in to view your provider profile';
    loading.value = false;
    return;
  }

  await loadProviderProfile();
});

// Function to load provider profile
async function loadProviderProfile() {
  try {
    loading.value = true;
    error.value = null;

    const data = await fetchProviderProfile(authToken.value);
    provider.value = data;

    // Fetch profile image if we have a provider ID
    if (data && data.id) {
      try {
        const imageResult = await getProviderProfileImage(data.id);
        if (imageResult.success && imageResult.image_url) {
          profileImageUrl.value = imageResult.image_url;
        }

        // Load facility images
        await loadFacilityImages(data.id);
      } catch (imageError) {
        console.error('Error loading profile image:', imageError);
        // Don't set main error, just log it since the profile loaded successfully
      }
    }
  } catch (err) {
    console.error('Failed to load provider profile:', err);
    error.value = 'Failed to load provider profile. Please try again later.';
  } finally {
    loading.value = false;
  }
}

// Load facility images
async function loadFacilityImages(providerId) {
  try {
    loadingImages.value = true;
    const result = await getProviderImages(providerId);

    if (result.success && result.images) {
      facilityImages.value = result.images;
    }
  } catch (err) {
    console.error('Failed to load facility images:', err);
  } finally {
    loadingImages.value = false;
  }
}

// Navigation functions - preserved from the original working code
function navigateToCreate() {
  router.push('/provider/manage');
}

function navigateToManage() {
  if (provider.value && provider.value.id) {
    router.push(`/provider/manage/${provider.value.id}`);
  } else {
    router.push('/provider/manage');
  }
}

function navigateToFacilityImages() {
  if (provider.value && provider.value.id) {
    router.push(`/provider/images/${provider.value.id}`);
  } else {
    error.value = "You must create a provider profile before managing images";
  }
}

// Helper function to get initials from name
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Format office hours for display
function formatOfficeHours(officeHours) {
  if (!officeHours || !officeHours.length) return [];

  // Sort by day of week
  const dayOrder = {
    'Monday': 0,
    'Tuesday': 1,
    'Wednesday': 2,
    'Thursday': 3,
    'Friday': 4,
    'Saturday': 5,
    'Sunday': 6
  };

  return [...officeHours].sort((a, b) => {
    return dayOrder[a.day] - dayOrder[b.day];
  });
}

// Calculate profile completeness - preserved the old logic but expanded
function calculateProfileCompleteness() {
  if (!provider.value) return 0;

  // Use existing completeness if available
  if (provider.value.profile_completeness !== undefined) {
    return provider.value.profile_completeness;
  }

  // Or calculate based on filled fields - keeping the original fields and adding new ones
  const requiredFields = [
    'provider_name',
    'specializations',
    'phone_numbers',
    'address_line1',
    'city',
    'additional_info',
    'client_types',
    'tags'
  ];

  let completedCount = 0;
  requiredFields.forEach(field => {
    if (Array.isArray(provider.value[field])) {
      if (provider.value[field] && provider.value[field].length > 0) completedCount++;
    } else if (provider.value[field]) {
      completedCount++;
    }
  });

  // Add bonus for additional fields
  if (profileImageUrl.value) {
    completedCount += 1;
  }

  if (facilityImages.value && facilityImages.value.length > 0) {
    completedCount += 1;
  }

  return Math.round((completedCount / (requiredFields.length + 2)) * 100);
}

// Handle image upload - preserved from the original working code
const handleImageUpload = async (options) => {
  const { file } = options;

  try {
    // Check if provider exists
    if (!provider.value || !provider.value.id) {
      imageUploadError.value = "You must create a provider profile before uploading an image";
      options.onError();
      return;
    }

    isUploadingImage.value = true;
    imageUploadError.value = null;

    console.log('[Upload] Original file:', file.file.name, 'Size:', file.file.size, 'bytes');

    // Check file size before compression
    if (file.file.size > 20 * 1024 * 1024) { // 20MB limit
      imageUploadError.value = "File too large. Please select a file smaller than 20MB.";
      options.onError();
      return;
    }

    // Compression options
    const compressionOptions = {
      maxSizeMB: 2,           // Max 2MB after compression
      maxWidthOrHeight: 1200, // Match your backend resize
      useWebWorker: true,
      fileType: 'image/webp', // Convert to WebP for better compression
      quality: 0.8            // 80% quality
    };

    // Compress the image
    const compressedFile = await imageCompression(file.file, compressionOptions);
    console.log('[Compression] Compressed file size:', compressedFile.size, 'bytes');
    console.log('[Compression] Size reduction:',
      Math.round((1 - compressedFile.size / file.file.size) * 100) + '%');

    // Create a new File object with original name but .webp extension
    const webpFileName = file.file.name.replace(/\.[^/.]+$/, '.webp');
    const finalFile = new File([compressedFile], webpFileName, {
      type: 'image/webp'
    });

    // Upload the compressed image
    const result = await uploadProviderProfileImage(authToken.value, finalFile);

    // Update profile image URL
    if (result && result.image_url) {
      profileImageUrl.value = result.image_url;
      options.onFinish();

      // Close modal after successful upload
      showImageModal.value = false;

      console.log('[Upload] Success! Image uploaded and URL updated');
    } else {
      imageUploadError.value = result.error || "Failed to upload image";
      options.onError();
    }
  } catch (err) {
    console.error('[Upload] Error:', err);

    // Better error messages
    if (err.message.includes('body limit') || err.message.includes('Payload Too Large')) {
      imageUploadError.value = "File too large. Please try compressing your image further.";
    } else if (err.message.includes('compression')) {
      imageUploadError.value = "Failed to compress image. Please try a different image.";
    } else {
      imageUploadError.value = err.message || "Failed to upload image";
    }

    options.onError();
  } finally {
    isUploadingImage.value = false;
  }
};

// Open image modal - only if provider exists
const openImageModal = () => {
  if (!provider.value || !provider.value.id) {
    error.value = "You must create a provider profile before uploading an image";
    return;
  }

  showImageModal.value = true;
  imageUploadError.value = null;
};

// Close image upload modal
const closeImageModal = () => {
  showImageModal.value = false;
  imageUploadError.value = null;
};

// View larger profile image
const viewLargerImage = () => {
  if (profileImageUrl.value) {
    window.open(profileImageUrl.value, '_blank');
  }
};

// Create view status text and color
const statusInfo = computed(() => {
  if (!provider.value) return { text: 'Not Created', color: 'error' };

  switch (provider.value.status) {
    case 'draft':
      return { text: 'Draft (Not Visible)', color: 'warning' };
    case 'unverified':
      return { text: 'Unverified', color: 'info' };
    case 'verified':
      return { text: 'Verified', color: 'success' };
    case 'suspended':
      return { text: 'Suspended', color: 'error' };
    case 'claimed':
      return { text: 'Claimed', color: 'success' };
    case 'unclaimed':
      return { text: 'Unclaimed', color: 'warning' };
    default:
      return { text: 'Unknown', color: 'default' };
  }
});
</script>

<template>
  <div class="provider-dashboard-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <NSpin size="large" />
      <p>Loading your profile...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <NAlert type="error" :title="error" />
      <NButton class="retry-button" @click="loadProviderProfile">Try Again</NButton>
    </div>

    <div v-else>
      <!-- Profile Header Card -->
      <NCard class="profile-header">
        <div class="profile-header-content">
          <div class="profile-avatar-container">
            <NPopover trigger="hover" placement="bottom" v-if="provider && provider.id">
              <template #trigger>
                <NAvatar round size="large" :src="profileImageUrl" class="profile-avatar" @click="viewLargerImage">
                  <template v-if="!profileImageUrl">
                    {{ getInitials(provider.provider_name) }}
                  </template>
                </NAvatar>
              </template>
              <div class="avatar-popover">
                <p>{{ profileImageUrl ? 'Click to view full image' : 'No profile image' }}</p>
                <NButton size="small" @click="openImageModal" type="primary" round>
                  <template #icon>
                    <NIcon>
                      <Camera />
                    </NIcon>
                  </template>
                  {{ profileImageUrl ? 'Change Image' : 'Add Image' }}
                </NButton>
              </div>
            </NPopover>

            <!-- If no provider profile exists, just show the avatar without the popover -->
            <NAvatar v-else round size="large" class="profile-avatar">
              ?
            </NAvatar>

            <!-- Camera button for profile image - only if provider exists -->
            <NButton v-if="provider && provider.id" class="camera-button" circle type="primary" @click="openImageModal"
              size="small">
              <NIcon>
                <Camera />
              </NIcon>
            </NButton>
          </div>

          <div class="profile-info">
            <h2>{{ provider?.provider_name || 'Your Legal Services Profile' }}</h2>
            <div v-if="provider && provider.provider_type" class="provider-type-display">
              {{ provider.provider_type }}
              <NTag :type="statusInfo.color" size="small">{{ statusInfo.text }}</NTag>
            </div>

            <div v-if="provider" class="profile-completeness">
              <div class="completeness-header">
                <span>Profile Completeness</span>
                <NTag :type="calculateProfileCompleteness() >= 70 ? 'success' : 'warning'" round>
                  {{ calculateProfileCompleteness() }}%
                </NTag>
              </div>
              <NProgress type="line" :percentage="calculateProfileCompleteness()"
                :processing="calculateProfileCompleteness() < 100"
                :status="calculateProfileCompleteness() >= 70 ? 'success' : 'warning'" :height="8" />
            </div>
          </div>

          <div class="profile-actions">
            <NSpace vertical size="small">
              <NButton v-if="provider" type="primary" @click="navigateToManage" round>
                <template #icon>
                  <NIcon>
                    <EditIcon />
                  </NIcon>
                </template>
                Update Profile
              </NButton>
              <NButton v-else type="primary" @click="navigateToCreate" round>
                <template #icon>
                  <NIcon>
                    <EditIcon />
                  </NIcon>
                </template>
                Create Profile
              </NButton>
            </NSpace>
          </div>
        </div>
      </NCard>

      <!-- Empty state -->
      <NCard v-if="!provider" class="profile-details">
        <NEmpty description="You don't have a legal services profile yet.">
          <template #extra>
            <p>You haven't created your legal services profile yet. Create your profile to be discoverable by potential clients.</p>
            <NButton type="primary" @click="navigateToCreate">Create Profile</NButton>
          </template>
        </NEmpty>
      </NCard>

      <!-- Provider profile content if exists -->
      <template v-else>
        <!-- Personal Info Section -->
        <NCard title="Personal Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <User />
            </NIcon>
          </template>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Attorney Name</div>
              <div class="info-value">{{ provider.provider_name || 'Not provided' }}</div>
            </div>

            <div class="info-item" v-if="provider.provider_type">
              <div class="info-label">Practice Type</div>
              <div class="info-value">{{ provider.provider_type }}</div>
            </div>

            <div class="info-item" v-if="provider.years_of_experience">
              <div class="info-label">Years of Experience</div>
              <div class="info-value">{{ provider.years_of_experience }}</div>
            </div>

            <div class="info-item" v-if="provider.phone_numbers && provider.phone_numbers.length > 0">
              <div class="info-label">
                <NIcon size="small">
                  <Phone />
                </NIcon>
                Phone
              </div>
              <div class="info-value phone-list">
                <div v-for="(phone, index) in provider.phone_numbers" :key="index" class="phone-number">
                  {{ phone }}
                </div>
              </div>
            </div>

            <div class="info-item" v-if="provider.website_url">
              <div class="info-label">
                <NIcon size="small">
                  <Globe />
                </NIcon>
                Website
              </div>
              <div class="info-value">
                <a :href="provider.website_url" target="_blank" rel="noopener">{{ provider.website_url }}</a>
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <MapPin />
                </NIcon>
                Location
              </div>
              <div class="info-value">
                <div v-if="provider.city || provider.state">
                  {{ provider.city }}{{ provider.city && provider.state ? ', ' : '' }}{{ provider.state }}
                  {{ provider.postal_code ? provider.postal_code : '' }}
                </div>
                <div v-else>Not provided</div>
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">Address</div>
              <div class="info-value">
                <div v-if="provider.address_line1">{{ provider.address_line1 }}</div>
                <div v-if="provider.address_line2">{{ provider.address_line2 }}</div>
                <div v-if="provider.address_line3">{{ provider.address_line3 }}</div>
                <div v-if="!provider.address_line1 && !provider.address_line2 && !provider.address_line3">Not provided
                </div>
              </div>
            </div>

            <div class="info-item" v-if="provider.social_media && provider.social_media.length > 0">
              <div class="info-label">
                <NIcon size="small">
                  <Link />
                </NIcon>
                Social Media
              </div>
              <div class="info-value social-links">
                <div v-for="(social, index) in provider.social_media" :key="index" class="social-item">
                  <strong>{{ social.platform }}:</strong>
                  <a :href="social.url.startsWith('http') ? social.url : 'https://' + social.url" target="_blank"
                    rel="noopener">{{ social.url }}</a>
                </div>
              </div>
            </div>
          </div>

          <div v-if="provider.additional_info" class="bio-section">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <MessageSquare />
              </NIcon>
              About
            </h3>
            <div class="notes-box">{{ provider.additional_info }}</div>
          </div>
        </NCard>

        <!-- Location Information -->
        <NCard v-if="provider.additional_locations && provider.additional_locations.length > 0"
          title="Additional Office Locations" class="profile-section">
          <template #header-extra>
            <NIcon>
              <MapPin />
            </NIcon>
          </template>

          <div class="locations-grid">
            <div v-for="(location, index) in provider.additional_locations" :key="index" class="location-item">
              <div class="location-address">
                <div>{{ location.address }}</div>
                <div>{{ location.city }}, {{ location.state }} {{ location.postal_code }}</div>
              </div>
            </div>
          </div>
        </NCard>

        <!-- Professional Information -->
        <NCard title="Professional Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Scale />
            </NIcon>
          </template>

          <div v-if="provider.specializations && provider.specializations.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Award />
              </NIcon>
              Practice Areas
            </h3>

            <NSpace>
              <NTag v-for="(spec, index) in provider.specializations" :key="index" type="info" round size="large"
                class="specialty-tag">
                {{ spec }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No practice areas provided</p>

          <NDivider />

          <div v-if="provider.services && provider.services.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Briefcase />
              </NIcon>
              Legal Services Offered
            </h3>

            <NSpace>
              <NTag v-for="(service, index) in provider.services" :key="index" type="primary" round size="medium"
                class="service-tag">
                {{ service }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No services listed</p>

          <NDivider />

          <div v-if="provider.client_types && provider.client_types.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Users />
              </NIcon>
              Client Types Served
            </h3>

            <NSpace>
              <NTag v-for="(clientType, index) in provider.client_types" :key="index" type="warning" round
                size="medium" class="client-tag">
                {{ clientType }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No client type information provided</p>

          <NDivider />

          <div v-if="provider.spoken_languages && provider.spoken_languages.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <LanguagesIcon />
              </NIcon>
              Spoken Languages
            </h3>

            <NSpace>
              <NTag v-for="(language, index) in provider.spoken_languages" :key="index" type="default" round
                size="medium" class="language-tag">
                {{ language }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No language information provided</p>

          <div v-if="provider.education && provider.education.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <School />
              </NIcon>
              Education
            </h3>

            <NTimeline>
              <NTimelineItem v-for="(edu, index) in provider.education" :key="index" :title="edu.degree"
                :content="edu.institution" :time="edu.year ? edu.year.toString() : ''" />
            </NTimeline>
          </div>

          <div v-if="provider.certifications && provider.certifications.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <FileText />
              </NIcon>
              Certifications
            </h3>

            <NTimeline>
              <NTimelineItem v-for="(cert, index) in provider.certifications" :key="index" :title="cert.name"
                :content="cert.issuer" :time="cert.year ? cert.year.toString() : ''" />
            </NTimeline>
          </div>

          <div v-if="provider.tags && provider.tags.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <MessageSquare />
              </NIcon>
              Tags
            </h3>

            <NSpace>
              <NTag v-for="(tag, index) in provider.tags" :key="index" type="success" round size="medium"
                class="tag-item">
                {{ tag }}
              </NTag>
            </NSpace>
          </div>
        </NCard>

        <!-- Legal Practice Details Section -->
        <NCard title="Legal Practice Details" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Gavel />
            </NIcon>
          </template>

          <div v-if="provider.court_representations && provider.court_representations.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Scale />
              </NIcon>
              Court Representations
            </h3>
            <NSpace>
              <NTag v-for="(court, index) in provider.court_representations" :key="index" type="primary" round size="medium"
                class="court-tag">
                {{ court }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No court representation information provided</p>

          <NDivider v-if="provider.court_representations && provider.court_representations.length > 0" />

          <div v-if="provider.case_types_handled && provider.case_types_handled.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <FileText />
              </NIcon>
              Case Types Handled
            </h3>
            <NSpace>
              <NTag v-for="(caseType, index) in provider.case_types_handled" :key="index" type="info" round size="medium"
                class="case-tag">
                {{ caseType }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No case type information provided</p>

          <NDivider v-if="provider.case_types_handled && provider.case_types_handled.length > 0" />

          <div v-if="provider.pro_bono_services && provider.pro_bono_services.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Heart />
              </NIcon>
              Pro Bono Services
            </h3>
            <NSpace>
              <NTag v-for="(service, index) in provider.pro_bono_services" :key="index" type="success" round size="medium"
                class="pro-bono-tag">
                {{ service }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No pro bono services listed</p>

          <div v-if="provider.affiliated_organizations && provider.affiliated_organizations.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Award />
              </NIcon>
              Bar Associations & Organizations
            </h3>
            <NSpace>
              <NTag v-for="(org, index) in provider.affiliated_organizations" :key="index" type="warning" round size="medium"
                class="organization-tag">
                {{ org }}
              </NTag>
            </NSpace>
          </div>

          <div v-if="provider.technology_available && provider.technology_available.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Globe />
              </NIcon>
              Legal Technology Available
            </h3>
            <NSpace>
              <NTag v-for="(tech, index) in provider.technology_available" :key="index" type="info" round size="medium"
                class="tech-tag">
                {{ tech }}
              </NTag>
            </NSpace>
          </div>

          <div v-if="provider.awards_accreditations && provider.awards_accreditations.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Award />
              </NIcon>
              Awards & Accreditations
            </h3>
            <NSpace>
              <NTag v-for="(award, index) in provider.awards_accreditations" :key="index" type="success" round size="medium"
                class="award-tag">
                {{ award }}
              </NTag>
            </NSpace>
          </div>

          <div v-if="provider.provider_staff && provider.provider_staff.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Users />
              </NIcon>
              Staff Members
            </h3>
            <div class="staff-grid">
              <div v-for="(staff, index) in provider.provider_staff" :key="index" class="staff-member">
                <div class="staff-name">{{ staff.name }}</div>
                <div class="staff-role">{{ staff.role }}</div>
                <div v-if="staff.qualifications" class="staff-qualifications">{{ staff.qualifications }}</div>
              </div>
            </div>
          </div>

          <div v-if="provider.services_offered && provider.services_offered.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Briefcase />
              </NIcon>
              Detailed Services Offered
            </h3>
            <NSpace>
              <NTag v-for="(service, index) in provider.services_offered" :key="index" type="primary" round size="medium"
                class="detailed-service-tag">
                {{ service }}
              </NTag>
            </NSpace>
          </div>
        </NCard>

        <!-- Practice Information -->
        <NCard v-if="provider.accepts_new_clients !== null ||
          provider.virtual_consultations !== null ||
          (provider.office_hours && provider.office_hours.length > 0) ||
          provider.parking_info ||
          (provider.payment_methods && provider.payment_methods.length > 0) ||
          (provider.accessibility_options && provider.accessibility_options.length > 0) ||
          provider.average_response_time_minutes !== null" title="Practice Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Clock />
            </NIcon>
          </template>

          <div class="info-grid">
            <div v-if="provider.accepts_new_clients !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <Heart />
                </NIcon>
                Accepting New Clients
              </div>
              <div class="info-value" :class="{
                'status-positive': provider.accepts_new_clients,
                'status-negative': !provider.accepts_new_clients
              }">
                {{ provider.accepts_new_clients ? 'Yes' : 'No' }}
              </div>
            </div>

            <div v-if="provider.virtual_consultations !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small"><Video /></NIcon>
                Virtual Consultations
              </div>
              <div class="info-value" :class="{
                'status-positive': provider.virtual_consultations,
                'status-negative': !provider.virtual_consultations
              }">
                {{ provider.virtual_consultations ? 'Yes' : 'No' }}
              </div>
            </div>

            <div v-if="provider.average_response_time_minutes !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <Clock />
                </NIcon>
                Average Response Time
              </div>
              <div class="info-value">
                {{ provider.average_response_time_minutes }} hours
              </div>
            </div>
          </div>

          <div v-if="provider.office_hours && provider.office_hours.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Clock />
              </NIcon>
              Office Hours
            </h3>

            <NTable :bordered="false" :single-line="false">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(hours, index) in formatOfficeHours(provider.office_hours)" :key="index">
                  <td>{{ hours.day }}</td>
                  <td>{{ hours.open }} - {{ hours.close }}</td>
                </tr>
              </tbody>
            </NTable>
          </div>

          <div v-if="provider.parking_info" class="parking-info" style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Car />
              </NIcon>
              Parking Information
            </h3>
            <div class="notes-box">{{ provider.parking_info }}</div>
          </div>

          <div v-if="provider.payment_methods && provider.payment_methods.length > 0" style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <CreditCard />
              </NIcon>
              Payment Methods
            </h3>

            <NSpace>
              <NTag v-for="(method, index) in provider.payment_methods" :key="index" type="info" round size="medium"
                class="payment-tag">
                {{ method }}
              </NTag>
            </NSpace>
          </div>

          <div v-if="provider.accessibility_options && provider.accessibility_options.length > 0"
            style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Accessibility />
              </NIcon>
              Accessibility Features
            </h3>

            <NSpace>
              <NTag v-for="(option, index) in provider.accessibility_options" :key="index" type="success" round
                size="medium" class="accessibility-tag">
                {{ option }}
              </NTag>
            </NSpace>
          </div>
        </NCard>

        <!-- Office Images -->
        <NCard v-if="facilityImages && facilityImages.length > 0" title="Office Images" class="profile-section">
          <template #header-extra>
            <NIcon>
              <ImageIcon />
            </NIcon>
          </template>

          <div class="facility-images-grid">
            <div v-for="(image, index) in facilityImages" :key="image.id" class="facility-image-card"
              @click="() => window.open(image.presigned_url, '_blank')">
              <img :src="image.presigned_url" :alt="`Office image ${index + 1}`" />
              <div class="image-overlay">
                <div class="image-type">{{ image.image_type || 'Office' }}</div>
                <div class="image-description" v-if="image.description">{{ image.description }}</div>
              </div>
            </div>
          </div>

          <div class="manage-images-button">
            <NButton @click="navigateToFacilityImages">
              <template #icon>
                <NIcon>
                  <ImageIcon />
                </NIcon>
              </template>
              Manage Office Images
            </NButton>
          </div>
        </NCard>

        <!-- Statistics Section -->
        <NCard title="Profile Statistics" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Calendar />
            </NIcon>
          </template>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Total Views</div>
              <div class="info-value stat-value">{{ provider.views || 0 }}</div>
            </div>

            <div class="info-item">
              <div class="info-label">Created</div>
              <div class="info-value">
                {{ provider.created_at ? new Date(provider.created_at).toLocaleDateString() : 'N/A' }}
              </div>
            </div>

            <div class="info-item" v-if="provider.updated_at">
              <div class="info-label">Last Updated</div>
              <div class="info-value">
                {{ new Date(provider.updated_at).toLocaleDateString() }}
              </div>
            </div>
          </div>
        </NCard>
      </template>
    </div>
  </div>

  <!-- Profile Image Upload Modal -->
  <NModal v-model:show="showImageModal" preset="card" title="Upload Profile Picture" :style="{ width: '400px' }">
    <template #header-extra>
      <NButton circle size="small" @click="closeImageModal">
        <NIcon>
          <CloseIcon />
        </NIcon>
      </NButton>
    </template>

    <div class="upload-modal-content">
      <div v-if="imageUploadError" class="upload-error">
        <NAlert type="error" :title="imageUploadError" closable />
      </div>

      <NUpload :custom-request="handleImageUpload" :max="1" accept="image/*" :show-file-list="false">
        <NUploadDragger>
          <div class="upload-dragger-content">
            <NIcon size="48" class="upload-icon">
              <UploadIcon />
            </NIcon>
            <p>Click or drag image to this area to upload</p>
            <p class="upload-hint">
              Support for JPEG, PNG, or GIF, max size 2MB
            </p>
          </div>
        </NUploadDragger>
      </NUpload>

      <div v-if="isUploadingImage" class="upload-loading">
        <NSpin size="medium" />
        <p>Uploading your image...</p>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer">
        <NButton @click="closeImageModal">Cancel</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.provider-dashboard-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.retry-button {
  margin-top: 16px;
}

.profile-header {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: #f9fbff;
  border: 1px solid #eaeffa;
}

.profile-header-content {
  display: flex;
  align-items: center;
  padding: 16px;
}

.profile-avatar-container {
  position: relative;
  margin-right: 24px;
}

.profile-avatar {
  cursor: pointer;
  width: 80px;
  height: 80px;
  font-size: 32px;
  background: linear-gradient(135deg, #2979ff, #1890ff);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
  transition: transform 0.2s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.camera-button {
  position: absolute;
  right: -5px;
  bottom: -5px;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.avatar-popover {
  padding: 8px;
  text-align: center;
}

.avatar-popover p {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin-top: 0;
  margin-bottom: 6px;
  font-size: 1.5rem;
  color: #0e639c;
  font-weight: 600;
}

.provider-type-display {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 1rem;
  color: #666;
}

.profile-completeness {
  width: 100%;
  max-width: 300px;
  margin-top: 8px;
}

.completeness-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.completeness-header span {
  font-size: 0.9rem;
  color: #516173;
  font-weight: 500;
}

.profile-actions {
  margin-left: 24px;
}

.profile-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #eaeffa;
}

.profile-section :deep(.n-card-header) {
  background-color: #f5f8ff;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0e639c;
  padding: 16px 24px;
}

.profile-section :deep(.n-card__content) {
  padding: 20px 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}

.info-item {
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.info-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.info-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.status-positive {
  color: #18a058;
}

.status-negative {
  color: #d03050;
}

.capitalize {
  text-transform: capitalize;
}

.phone-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phone-number {
  font-family: monospace;
  letter-spacing: 0.5px;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.social-item {
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.social-item a {
  color: #1890ff;
  text-decoration: none;
  transition: color 0.2s ease;
}

.social-item a:hover {
  color: #0070e0;
  text-decoration: underline;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  color: #0e639c;
  margin-top: 24px;
  margin-bottom: 16px;
}

.section-icon {
  margin-right: 8px;
  color: #1890ff;
}

.empty-list-message {
  color: #8c9bab;
  font-style: italic;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  font-size: 0.95rem;
}

.specialty-tag,
.tag-item,
.client-tag,
.service-tag,
.language-tag,
.payment-tag,
.accessibility-tag,
.court-tag,
.case-tag,
.pro-bono-tag,
.organization-tag,
.tech-tag,
.award-tag,
.detailed-service-tag {
  margin: 4px;
  padding: 6px 12px;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.specialty-tag:hover,
.tag-item:hover,
.client-tag:hover,
.service-tag:hover,
.language-tag:hover,
.payment-tag:hover,
.accessibility-tag:hover,
.court-tag:hover,
.case-tag:hover,
.pro-bono-tag:hover,
.organization-tag:hover,
.tech-tag:hover,
.award-tag:hover,
.detailed-service-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
}

.notes-box {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
  white-space: pre-line;
  line-height: 1.6;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1890ff;
}

.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.location-item {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid #18a058;
  transition: all 0.2s ease;
}

.location-item:hover {
  background-color: #f0f9f5;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.location-address {
  line-height: 1.6;
}

/* Staff grid */
.staff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.staff-member {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid #1890ff;
  transition: all 0.2s ease;
}

.staff-member:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.staff-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #0e639c;
  margin-bottom: 4px;
}

.staff-role {
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.staff-qualifications {
  font-size: 0.9rem;
  color: #888;
  font-style: italic;
}

/* Office images grid */
.facility-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.facility-image-card {
  position: relative;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.facility-image-card:hover {
  transform: scale(1.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.facility-image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 12px;
  font-size: 0.85rem;
}

.image-type {
  font-weight: 600;
  margin-bottom: 4px;
}

.image-description {
  font-size: 0.75rem;
  opacity: 0.9;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.manage-images-button {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

/* Upload modal styling */
.upload-modal-content {
  padding: 16px 0;
}

.upload-dragger-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.upload-icon {
  color: #1890ff;
  margin-bottom: 16px;
}

.upload-hint {
  font-size: 0.85rem;
  color: #999;
  margin-top: 8px;
}

.upload-error {
  margin-bottom: 16px;
}

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .provider-dashboard-container {
    padding: 10px;
  }

  .profile-header-content {
    flex-direction: column;
    text-align: center;
  }

  .profile-avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }

  .profile-actions {
    margin-left: 0;
    margin-top: 15px;
  }

  .profile-completeness {
    max-width: 100%;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .staff-grid {
    grid-template-columns: 1fr;
  }

  .facility-images-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .facility-image-card {
    height: 120px;
  }
}
</style>